import React, { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import auth from "helpers/auth";
import { API, BASE_URL, ESTADOS_MOVIMIENTOS, ESTADOS_LINEAS } from "CONST";
import { get } from 'helpers/requests';
import BaseTable from "components/shared/BaseTable";
import {
  determineMovementStatus,
  cellStatus,
  cellNumOperation,
  cellOperation,
  headerDoubleRowOperation,
  headerOperation
} from "helpers/tables";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faInfoCircle, faQuestionCircle, faComments } from "@fortawesome/free-solid-svg-icons";
import { fileDownload } from "../../../../helpers/helpers";
import Icon from "../../../UI/Icon";
import CreditLineDetailComentariosModal from './CreditLineDetailComentariosModal';


const CreditLineDetailTable = ({ clienteId, id, poliza_nemotecnico, terminoVigenciaPoliza, monedaAsegurada }) => {
  const [ movimientos, setMovimientos ] = useState([]);
  const [ loading, setLoading ] = useState(false);
  const isCurrent = useRef(true);
  const loginIdToken = useSelector((state) => state.login.id_token);
  const user = useSelector((state) => state.login.user)

  const [comentarios, setComentarios] = useState([]);
  const [openComentariosModal, setOpenComentariosModal] = useState(false);
  const [movimientoIdComentarios, setMovimientoIdComentarios] = useState();

  const fetchMovimientos = async (isFullReload) => {
    if (isFullReload) {
      setMovimientos([]);
      setLoading(true);

      // Eliminar la llamada a polizaPromise
      const movimientosPromise = auth.fetch(`${BASE_URL}${API.movimientosLineaCredito}/?linea_credito_id=${id}&client_id=${clienteId}`)
        .then(result => {
          const movimientosConStatus = determineMovementStatus(result);
          setMovimientos(movimientosConStatus.map((l, idx) => ({ ...l, number: movimientosConStatus.length - idx })));

          if (movimientosConStatus.some(obj => obj.estado_data?.nemotecnico === ESTADOS_MOVIMIENTOS.procesando)) {
            setTimeout(() => {
              fetchMovimientos(false);
            }, 5000);
          }
        });

      await Promise.all([movimientosPromise]);
      if (isCurrent.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      // Se llama cuando el componente esta a punto de desmontarse
      isCurrent.current = false;
    }
  }, []);

  useEffect(() => {
    if (clienteId) {
      fetchMovimientos(true);
    }
  }, [clienteId]);

  const closeComentariosModal = () => {
    setOpenComentariosModal(false);
  };

  const handleComentariosModal = (row) => {
    setMovimientoIdComentarios(row.original.id);
    setComentarios(row.original.comentarios);
    setOpenComentariosModal(true);
  };

  const columns=[
    {
      Header: headerOperation("#"),
      id: "number",
      accessor: i => cellOperation(i.number),
      sortable: false,
      width: 40,
    },
    {
      Header: headerDoubleRowOperation("Tipo", "Solicitud"),
      id: "tipo_solicitud",
      accessor: i => {
        if (i.tipo?.nemotecnico && ["deshabilitar", "reducir", "bloqueo"].includes(i.tipo.nemotecnico)) {
          return cellOperation("-", "center");
        }
        return cellOperation(
          i.is_migration ? (
            <div>
              {i.tipo.descripcion}
              <Icon
                id={`check-${i.id}`}
                placement="right"
                tooltipText="Este movimiento fue cargado en una migración histórica. Dirigirse al sitio de Credex para buscar el historial previo."
              >
                <FontAwesomeIcon style={{ color: "var(--orsan-blue)" }} icon={faQuestionCircle} />
              </Icon>
            </div>
          ) : i.tipo.descripcion,
          "center"
        );
      },
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation("Es", "Temporal"),
      id: "es_linea_temporal",
      accessor: i => {
        //console.log("terminoVigenciaPoliza: ", terminoVigenciaPoliza);
        //console.log("i.fecha_fin_vigencia: ", i.fecha_fin_vigencia);
        if (!terminoVigenciaPoliza || !i.fecha_fin_vigencia || i.monto_otorgado === 0) {
          return cellOperation("-", "center");
        }    
        const formatoISO = new Date(terminoVigenciaPoliza);
        const [dia, mes, ano] = i.fecha_fin_vigencia.split("-");
        const formatoFechaFin = new Date(`${ano}-${mes}-${dia}`);
        
        return cellOperation(
          formatoFechaFin.getTime() !== formatoISO.getTime() ? "Si" : "No",
          "center"
        );
      },
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation("Fecha", "Solicitud"),
      id: "fec_solicitud",
      accessor: i => cellOperation(i.fecha_solicitud),
      sortable: false,
      minWidth: 100,
    },
    // {
    //   Header: headerDoubleRowOperation("Monto", "Vigente (UF)"),
    //   id: "vigente",
    //   accessor: i => cellNumOperation(i.monto_vigente),
    //   sortable: false
    // },
    {
      Header: headerDoubleRowOperation("Monto", `Otorgado (${monedaAsegurada})`),
      id: "otorgado",
      accessor: i => cellNumOperation(i.monto_otorgado),
      sortable: false
    },
    {
      Header: headerDoubleRowOperation("Monto", `Solicitado (${monedaAsegurada})`),
      id: "solicitado",
      accessor: i => cellNumOperation(i.monto_solicitado),
      sortable: false,
    },
    {
      Header: headerDoubleRowOperation("Observaciones del", "Asegurado"),
      id: "observacion",
      accessor: i => cellOperation(i.observacion),
      sortable: false,
      minWidth: 120,
    },
    {
      Header: headerDoubleRowOperation("Fecha", "Respuesta"),
      id: "fec_respuesta",
      accessor: i => {
        if (i.estado_data.nemotecnico === ESTADOS_MOVIMIENTOS.pendiente) {
          return cellOperation('-');
        }
        return cellOperation(i.fecha_respuesta)
      },
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation("Fecha Inicio", "de Cobertura"),
      id: "fec_vigencia",
      accessor: i => cellOperation(i.monto_otorgado === 0 ? '-' : i.fecha_vigencia),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation("Fecha Fin", "de Cobertura"),
      id: "fec_fin_cobertura",
      accessor: i => cellOperation(i.monto_otorgado === 0 ? '-' : i.fecha_fin_vigencia),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerOperation("Resolución"),
      id: "resolucion",
      accessor: "estado_data",
      Cell: (row) => {
        if (row.original?.tipo.nemotecnico === "reducir") {
          return cellOperation("Reducida", "center");
        }
        if (row.original?.estado_linea === 7) {
          return cellOperation("Deshabilitada", "center");
        }
        return cellOperation(
          row.value ? (
            <div>
              {row.value.descripcion}
              &nbsp;
              {row.value.nemotecnico === ESTADOS_MOVIMIENTOS.error && (
                <>
                  <Icon
                    id={`obs-${row.original.id}`}
                    placement="right"
                    tooltipText={(
                      <div>
                        Ocurrió un error y no se pudo enviar su solicitud a analizar.
                      </div>
                    )}
                  >
                    <FontAwesomeIcon style={{ color: "var(--orsan-blue)" }} icon={faInfoCircle} />
                  </Icon>
                  <div>No se pudo procesar su solicitud. Por favor vuelva a intentarlo.</div>
                </>
              )}
            </div>
          ) : "Pendiente",
          "center"
        );
      },
      sortable: false,
      minWidth: 120,
    },    
    {
      Header: headerDoubleRowOperation("Estado"),
      id: "estado_vigencia",
      accessor: (row) => cellStatus(row.status),
      sortable: false,
      minWidth: 75,
    },
    {
      Header: headerOperation("Motivo"),
      id: "motivo",
      accessor: "motivo",
      Cell: (row) =>cellOperation(row.original.motivo),
      minWidth: 120,
    },
    {
      Header: headerDoubleRowOperation("Comentario", "al Asegurado"),
      id: "comentario",
      accessor: i => cellOperation(i.comentario_asegurado),
      sortable: false,
      width: 200
    },
    {
      Header: headerDoubleRowOperation("Carpeta", "Tributaria"),
      id: "carpeta_tributaria",
      accessor: "carpeta_tributaria_id",
      Cell: (row) => row.value  ? cellOperation((
        <button
          type="button"
          className="table-link"
          style={{ fontSize: "18px", marginLeft: "10px" }}
          onClick={() => (
            fileDownload(
              `${BASE_URL}${API.movimientosLineaCredito}/${row.original.id}/tributaria`,
              loginIdToken,
            )
          )}
          title="descargar"
        >
          <FontAwesomeIcon icon={faDownload} />
        </button>
      )) : "",
      sortable: false
    },
    {
      Header: headerOperation("Documentos"),
      id: "documentos",
      accessor: "documentos_ids",
      Cell: (row) => row.value.length > 0 ? cellOperation((
        <button
          type="button"
          className="table-link"
          style={{ fontSize: "18px", marginLeft: "10px" }}
          onClick={() => (
            fileDownload(
              `${BASE_URL}${API.movimientosLineaCredito}/${row.original.id}/download`,
              loginIdToken,
            )
          )}
          title="descargar"
        >
          <FontAwesomeIcon icon={faDownload} />
        </button>
      )) : "",
      sortable: false
    },
  ]

  if(user.is_orsan){
    columns.push(
      {
        Header: headerOperation("Comentarios"),
        id: "comentarios",
        accessor: "comentarios",
        Cell: (row) => cellOperation(row.value.length > 0 && (
          <Button
            id='comentarios'
            className="table-link download"
            onClick={() => handleComentariosModal(row)}
          >
            <FontAwesomeIcon icon={faComments} />
          </Button>
        )),
        minWidth: 100,
        sortable: false,
      },
    )
  }

  return (
    <>
      <BaseTable
        loading={loading}
        data={movimientos}
        columns={columns}
        hideLoadingText
      />
      <CreditLineDetailComentariosModal
        openModal={openComentariosModal}
        closeModal={closeComentariosModal}
        comentarios={comentarios}
        movimientoIdComentarios={movimientoIdComentarios}
      />
    </>
  );
};

CreditLineDetailTable.propTypes = {
  id: PropTypes.string.isRequired,
  clienteId: PropTypes.number,
  terminoVigenciaPoliza: PropTypes.string,
  monedaAsegurada: PropTypes.string,
};

CreditLineDetailTable.defaultProps = {
  clienteId: null,
  monedaAsegurada: 'UF',
};

export default CreditLineDetailTable;
